import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  Grid,
  Typography,
} from '@mui/material';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  useGetCO2eEmissionDataQuery,
  useGetCO2eEmissionTimestampSummaryQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import TollRoundedIcon from '@mui/icons-material/TollRounded';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { usePutEnergyGroupOptionsMutation } from '../../../../api/recalcV2/endpoints/energyGroupsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import { printFlagsList } from '@/layout/pages/EnergyXplorer/printFlags';
import { useTranslation } from 'react-i18next';
import LoadingCard from '@/features/energyxplorer/components/card/LoadingCard';
import DeclarationCard from '@/features/energyxplorer/components/esg/DeclarationCard';
import GoCard from '@/features/energyxplorer/components/esg/GoCard';
import KpiCard from '@/features/energyxplorer/components/esg/KpiCard';
import SeuDatagrid from '@/features/energyxplorer/components/esg/SeuDatagrid';
import GoSharesDatagrid from '@/features/energyxplorer/components/esg/GoSharesDatagrid';
import EsgDistributionPieChart from '@/features/energyxplorer/components/esg/EsgDistributionPieChart';
import YearOnYearGraph from '@/features/energyxplorer/components/esg/YearOnYearGraph';

const EsgOverviewTab = (props) => {
  const {
    selectedEnergyGroup,
    selectedAccount,
    timeRange,
    setTimeRange,
    printFlags = printFlagsList.esg,
  } = props;

  const { t } = useTranslation('energyxplorer-esg');

  const dispatch = useDispatch();

  const {
    data: timestampsSummary,
    isSuccess: isTimestampsSummarySuccess,
    isFetching: isTimestampsSummaryFetching,
  } = useGetCO2eEmissionTimestampSummaryQuery(
    {
      accountId: selectedAccount?.AccountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      timerange: timeRange,
    },
    {
      skip: !selectedAccount?.AccountId || selectedEnergyGroup === null,
    },
  );

  const isTimestampCorrected = useMemo(() => {
    if (!isTimestampsSummarySuccess || !timestampsSummary) return false;

    const maxEarliest = DateTime.fromISO(
      timestampsSummary.maxEarliestDataTimestamp || '',
    );
    const minLatest = DateTime.fromISO(
      timestampsSummary.minLatestDataTimestamp || '',
    );

    const currentStart = DateTime.fromMillis(timeRange.start);
    const currentEnd = DateTime.fromMillis(timeRange.end);

    return currentStart >= maxEarliest && currentEnd <= minLatest;
  }, [
    isTimestampsSummarySuccess,
    timestampsSummary,
    timeRange.start,
    timeRange.end,
  ]);
  const {
    data: esgOverviewData,
    isError: isEsgOverviewDataError,
    isFetching: isEsgOverviewDataFetching,
    isSuccess: isEsgOverviewDataSuccess,
  } = useGetCO2eEmissionDataQuery(
    {
      accountId: selectedAccount?.AccountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange?.start,
      end: timeRange?.end,
    },
    {
      skip:
        !selectedAccount?.AccountId ||
        selectedEnergyGroup === null ||
        timeRange.start === null ||
        timeRange.end === null ||
        !isTimestampCorrected,
    },
  );

  const modes = [
    {
      id: 0,
      areaType: 'location',
      icon: <PlaceRoundedIcon />,
      energySummary: esgOverviewData?.Location?.CO2eEmissionsSummary,
    },
    {
      id: 1,
      areaType: 'municipality',
      icon: <LocationCityRoundedIcon />,
      energySummary: esgOverviewData?.Municipality?.CO2eEmissionsSummary,
    },
    {
      id: 2,
      areaType: 'market',
      icon: <TollRoundedIcon />,
      energySummary: esgOverviewData?.Market?.CO2eEmissionsSummary,
    },
  ];
  const [selectedMode, setSelectedMode] = React.useState(0);

  const scopeData = useMemo(() => {
    switch (selectedMode) {
      case 0:
        return esgOverviewData?.Location;
      case 1:
        return esgOverviewData?.Municipality;
      case 2:
        return esgOverviewData?.Market;
      default:
        return null;
    }
  }, [selectedMode, esgOverviewData]);

  const gridMixSummary = useMemo(() => scopeData?.GridMixSummary, [scopeData]);
  const reShare = useMemo(
    () => scopeData?.REShareSummary?.REShare,
    [scopeData],
  );
  const gridMixData = useMemo(
    () => scopeData?.GridMixDistributionData,
    [scopeData],
  );
  const seuData = useMemo(() => scopeData?.MonthlyCO2eEmissions, [scopeData]);

  useEffect(() => {
    const handleAdjustTimeRange = (tr) => {
      if (!tr) return;

      const maxEarliest = DateTime.fromISO(tr.maxEarliestDataTimestamp || '');
      const minLatest = DateTime.fromISO(tr.minLatestDataTimestamp || '');
      const currentStart = DateTime.fromMillis(timeRange.start);
      const currentEnd = DateTime.fromMillis(timeRange.end);

      const isCompletelyOutside =
        currentEnd < maxEarliest || currentStart > minLatest;

      const isCompletelyInside =
        currentStart >= maxEarliest && currentEnd <= minLatest;

      if (isCompletelyInside) return;

      let adjustedStart;
      let adjustedEnd;

      if (isCompletelyOutside) {
        // Full fallback: select full available window
        adjustedStart = maxEarliest;
        adjustedEnd = minLatest;
      } else {
        // Normal adjustment: clip only start or end
        adjustedStart = currentStart < maxEarliest ? maxEarliest : currentStart;
        adjustedEnd = currentEnd > minLatest ? minLatest : currentEnd;

        // Extra safety: if adjustment somehow causes inverted range
        if (adjustedStart > adjustedEnd) {
          adjustedStart = maxEarliest;
          adjustedEnd = minLatest;
        }
      }

      setTimeRange({
        start: adjustedStart.toUTC().toMillis(),
        end: adjustedEnd.toUTC().toMillis(),
      });

      dispatch(
        showSnackbar({
          severity: 'info',
          message: t('snackbar.timerange_changed'),
        }),
      );
    };

    handleAdjustTimeRange(timestampsSummary);
  }, [
    t,
    dispatch,
    setTimeRange,
    timeRange.end,
    timeRange.start,
    timestampsSummary,
  ]);

  const [openGoShares, setOpenGoShares] = useState(false);

  const handleCloseGoShares = () => {
    setOpenGoShares(false);
  };

  const [putEnergyGroupOptions] = usePutEnergyGroupOptionsMutation();

  const handleSaveChanges = async (goShares) => {
    if (!selectedEnergyGroup?.energyGroupId) return;

    const options = selectedEnergyGroup?.options;

    const updates = {
      ...options,
      goShares: goShares,
    };

    try {
      await putEnergyGroupOptions({
        accountId: selectedAccount.AccountId,
        energyGroupId: selectedEnergyGroup.energyGroupId,
        options: updates,
      });
      dispatch(
        showSnackbar({
          message: t('snackbar.go_share_update.success'),
          severity: 'success',
        }),
      );
    } catch (err) {
      console.error('Error updating energy group:', err);
      dispatch(
        showSnackbar({
          message: t('snackbar.go_share_update.error'),
          severity: 'error',
        }),
      );
    }
  };

  if (isEsgOverviewDataFetching || isTimestampsSummaryFetching) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%', // Keeps it within the available space without forcing full height
        }}
      >
        <LoadingCard>
          <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
            {t('loading_page')}
          </Typography>
        </LoadingCard>
      </Box>
    );
  }

  if (isEsgOverviewDataError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Card
          width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}
          variant="outlined"
          sx={{ p: 2, mb: 2, mt: 8, borderRadius: 2 }}
        >
          <CardContent width={{ xs: '100%', sm: '80%', md: '60%', lg: '60%' }}>
            <Typography variant="h6">{t('error_card.title')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t('error_card.description')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <>
      {isEsgOverviewDataSuccess && (
        <Box>
          <Grid container spacing={2} paddingTop={0}>
            {printFlags.declaration &&
              modes.map((mode) => (
                <Grid
                  key={mode.id}
                  item
                  md={12}
                  lg={6}
                  xl={3}
                  className={mode.id === selectedMode ? '' : 'non-printable'}
                >
                  <Suspense fallback={<div>Loading...</div>}>
                    <DeclarationCard
                      key={mode.id}
                      {...mode}
                      timeRange={timeRange}
                      mode={mode.id}
                      setMode={setSelectedMode}
                      selected={mode.id === selectedMode}
                    />
                  </Suspense>
                </Grid>
              ))}
            <Grid item md={12} lg={6} xl={3}>
              {printFlags.reshare &&
                (selectedMode === 2 ? (
                  <Suspense fallback={<div>Loading...</div>}>
                    <GoCard
                      openGoShares={setOpenGoShares}
                      number={reShare}
                      timeRange={timeRange}
                    />
                  </Suspense>
                ) : (
                  <Suspense fallback={<div>Loading...</div>}>
                    <KpiCard
                      title={t('re_card.label')}
                      description={t('re_card.description')}
                      number={reShare}
                      unit={`%`}
                      timeRange={timeRange}
                    />
                  </Suspense>
                ))}
            </Grid>
            {printFlags.distribution && selectedMode !== 2 && (
              <Suspense fallback={<div>Loading...</div>}>
                <EsgDistributionPieChart
                  GridMixData={gridMixData}
                  GridMixLocationSummary={gridMixSummary}
                  timeRange={timeRange}
                />
              </Suspense>
            )}

            {printFlags.seudatagrid && (
              <Grid item md={12} sx={{ transition: 'flex-basis 0.3s ease' }}>
                <Suspense fallback={<div>Loading...</div>}>
                  <SeuDatagrid data={seuData} />
                </Suspense>
              </Grid>
            )}
            {printFlags.yoy && (
              <Grid item md={12} sx={{ transition: 'flex-basis 0.3s ease' }}>
                <Suspense fallback={<div>Loading...</div>}>
                  <YearOnYearGraph data={seuData} />
                </Suspense>
              </Grid>
            )}
          </Grid>
          <Dialog
            open={openGoShares}
            onClose={handleCloseGoShares}
            maxWidth="lg"
          >
            <Suspense fallback={<div>Loading...</div>}>
              <GoSharesDatagrid
                handleSaveChanges={handleSaveChanges}
                initialRows={selectedEnergyGroup?.options?.goShares}
              />
            </Suspense>
          </Dialog>
        </Box>
      )}
    </>
  );
};

export default EsgOverviewTab;
